import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation, useScroll, useTransform } from 'framer-motion';
import { ArrowUp } from 'lucide-react';

// Enhanced Animated Particles with scroll-based density, opacity, and glow
function AnimatedParticles({ triggerBurst, scrollYProgress }) {
  const baseParticleCount = 80;
  const maxParticleCount = 120;
  const particleCount = useTransform(scrollYProgress, [0, 1], [baseParticleCount, maxParticleCount]);
  const particleOpacity = useTransform(scrollYProgress, [0, 1], [0.5, 0.7]);
  const particleGlow = useTransform(scrollYProgress, [0, 1], [12, 18]);

  const [currentParticleCount, setCurrentParticleCount] = useState(baseParticleCount);

  useEffect(() => {
    const unsubscribe = particleCount.onChange((latest) => {
      setCurrentParticleCount(Math.floor(latest));
    });
    return () => unsubscribe();
  }, [particleCount]);

  const particles = Array.from({ length: currentParticleCount }, (_, i) => ({
    id: i,
    x: Math.random() * window.innerWidth,
    y: Math.random() * 400,
    size: Math.random() * 5 + 2,
    speedX: (Math.random() - 0.5) * (1.3 + scrollYProgress.get() * 0.7),
    speedY: (Math.random() - 0.5) * (1.3 + scrollYProgress.get() * 0.7),
    glow: Math.random() * 0.6 + 0.4,
    color: ['#3b82f6', '#a855f7', '#ec4899'][Math.floor(Math.random() * 3)],
  }));

  const [particlePositions, setParticlePositions] = useState(particles);
  const [burstParticles, setBurstParticles] = useState([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setParticlePositions((prev) =>
        prev.slice(0, currentParticleCount).map((particle) => {
          let newX = particle.x + particle.speedX;
          let newY = particle.y + particle.speedY;

          if (newX < 0) newX = window.innerWidth;
          if (newX > window.innerWidth) newX = 0;
          if (newY < 0) newY = 400;
          if (newY > 400) newY = 0;

          return { ...particle, x: newX, y: newY };
        })
      );
    }, 20);

    return () => clearInterval(interval);
  }, [currentParticleCount]);

  useEffect(() => {
    if (triggerBurst) {
      const newBurst = Array.from({ length: 25 }, (_, i) => ({
        id: `burst-${i}-${Date.now()}`,
        x: window.innerWidth / 2,
        y: 200,
        size: Math.random() * 7 + 3,
        speedX: (Math.random() - 0.5) * 10,
        speedY: (Math.random() - 0.5) * 10,
        color: ['#3b82f6', '#a855f7', '#ec4899'][Math.floor(Math.random() * 3)],
        opacity: 1,
      }));
      setBurstParticles(newBurst);

      setTimeout(() => {
        setBurstParticles([]);
      }, 1200);
    }
  }, [triggerBurst]);

  return (
    <div className="absolute inset-0 pointer-events-none">
      {particlePositions.map((particle) => (
        <motion.div
          key={particle.id}
          initial={{ x: particle.x, y: particle.y, opacity: 0.8 }}
          animate={{
            x: particle.x,
            y: particle.y,
            scale: [1, 1.3, 1],
            opacity: particleOpacity,
          }}
          transition={{ duration: 1.8, repeat: Infinity, repeatType: 'loop' }}
          className="absolute rounded-full"
          style={{
            width: particle.size,
            height: particle.size,
            background: particle.color,
            boxShadow: `0 0 ${particleGlow.get() * particle.glow}px ${particle.color}`,
          }}
        />
      ))}
      {burstParticles.map((particle) => (
        <motion.div
          key={particle.id}
          initial={{ x: particle.x, y: particle.y, opacity: 1 }}
          animate={{
            x: particle.x + particle.speedX * 12,
            y: particle.y + particle.speedY * 12,
            opacity: 0,
            scale: [1, 0.5],
          }}
          transition={{ duration: 1, ease: 'easeOut' }}
          className="absolute rounded-full"
          style={{
            width: particle.size,
            height: particle.size,
            background: particle.color,
            boxShadow: `0 0 12px ${particle.color}`,
          }}
        />
      ))}
    </div>
  );
}

// Contact Sparkles for hover effect
function ContactSparkles({ trigger }) {
  const [sparkles, setSparkles] = useState([]);

  useEffect(() => {
    if (trigger) {
      const newSparkles = Array.from({ length: 5 }, (_, i) => ({
        id: `sparkle-${i}-${Date.now()}`,
        x: Math.random() * 100,
        y: Math.random() * 20,
        size: Math.random() * 3 + 2,
        speedX: (Math.random() - 0.5) * 5,
        speedY: (Math.random() - 0.5) * 5,
        color: ['#3b82f6', '#a855f7'][Math.floor(Math.random() * 2)],
      }));
      setSparkles(newSparkles);

      setTimeout(() => {
        setSparkles([]);
      }, 800);
    }
  }, [trigger]);

  return (
    <div className="absolute inset-0 pointer-events-none">
      {sparkles.map((sparkle) => (
        <motion.div
          key={sparkle.id}
          initial={{ x: sparkle.x, y: sparkle.y, opacity: 1 }}
          animate={{
            x: sparkle.x + sparkle.speedX * 10,
            y: sparkle.y + sparkle.speedY * 10,
            opacity: 0,
            scale: [1, 0.3],
          }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="absolute rounded-full"
          style={{
            width: sparkle.size,
            height: sparkle.size,
            background: sparkle.color,
            boxShadow: `0 0 8px ${sparkle.color}`,
          }}
        />
      ))}
    </div>
  );
}

// Social Icon matching the original footer
function SocialIcon3D({ href, imageSrc, alt, scrollYProgress }) {
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 5]);
  const shadowIntensity = useTransform(scrollYProgress, [0, 1], ['0 4px 15px rgba(147, 51, 234, 0.4)', '0 4px 25px rgba(147, 51, 234, 0.6)']);

  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="mx-4 transition-all duration-300 relative"
      whileHover={{ y: -8, rotateX: 15, rotateY: 15, scale: 1.2 }}
      whileTap={{ scale: 0.95 }}
      style={{ perspective: '1000px', scale, rotate }}
    >
      <motion.div
        className="rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center"
        style={{
          width: '48px',
          height: '48px',
          boxShadow: shadowIntensity,
        }}
        animate={{
          scale: [1, 1.1, 1],
          boxShadow: [
            '0 4px 15px rgba(147, 51, 234, 0.4)',
            '0 4px 25px rgba(147, 51, 234, 0.6)',
          ],
        }}
        transition={{ duration: 1.5, repeat: Infinity, repeatType: 'reverse' }}
      >
        <img src={imageSrc} alt={alt} className="w-8 h-8 object-contain" />
      </motion.div>
      <motion.div
        className="absolute bottom-0 left-1/2 w-8 h-2 bg-purple-900/30 rounded-full blur-sm"
        initial={{ scale: 0.8, opacity: 0.3 }}
        whileHover={{ scale: 1.2, opacity: 0.5 }}
        transition={{ duration: 0.3 }}
      />
    </motion.a>
  );
}

export default function Footer() {
  const controls = useAnimation();
  const footerRef = useRef(null);
  const [logoClicked, setLogoClicked] = useState(false);
  const [emailHover, setEmailHover] = useState(false);
  const [phoneHover, setPhoneHover] = useState(false);
  const [emailClick, setEmailClick] = useState(false);
  const [phoneClick, setPhoneClick] = useState(false);

  const { scrollYProgress } = useScroll({
    target: footerRef,
    offset: ['start end', 'end end'],
  });

  const borderRadius = useTransform(scrollYProgress, [0, 1], ['40px', '0px']);
  const glowIntensity = useTransform(scrollYProgress, [0, 1], ['0 0 10px rgba(147, 51, 234, 0.3)', '0 0 20px rgba(147, 51, 234, 0.5)']);
  const brandingFontSize = useTransform(scrollYProgress, [0, 1], ['1.875rem', '2.25rem']);
  const contactGlow = useTransform(scrollYProgress, [0, 1], ['0 0 5px rgba(147, 51, 234, 0.3)', '0 0 15px rgba(147, 51, 234, 0.5)']);
  const copyrightGlow = useTransform(scrollYProgress, [0, 1], ['0 0 0px rgba(168, 85, 247, 0)', '0 0 10px rgba(168, 85, 247, 0.5)']);

  useEffect(() => {
    controls.start({
      opacity: 1,
      y: 0,
      transition: { duration: 1, ease: 'easeOut' },
    });
  }, [controls]);

  const handleLogoClick = () => {
    setLogoClicked(!logoClicked);
  };

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Typewriter effect for branding text
  const brandingText = 'AI Empower';
  const brandingVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };
  const letterVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div style={{ background: 'black' }}>
      <motion.footer
        ref={footerRef}
        initial={{ opacity: 0, y: 80 }}
        animate={controls}
        className="relative bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 py-16 w-full text-gray-200 overflow-hidden"
        style={{
          borderTopLeftRadius: borderRadius,
          borderTopRightRadius: borderRadius,
        }}
      >
        {/* Subtle Animated Gradient Border */}
        <motion.div
          className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-blue-500/50 via-purple-500/50 to-pink-500/50"
          animate={{
            backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
            boxShadow: [
              '0 0 10px rgba(147, 51, 234, 0.3)',
              '0 0 10px rgba(59, 130, 246, 0.4)',
              '0 0 10px rgba(147, 51, 234, 0.3)',
            ],
          }}
          style={{
            backgroundSize: '200% 200%',
            backgroundImage: 'linear-gradient(90deg, #3b82f6, #a855f7, #ec4899, #3b82f6)',
          }}
          transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
        />

        <AnimatedParticles triggerBurst={logoClicked} scrollYProgress={scrollYProgress} />

        <div className="relative max-w-7xl mx-auto px-6 min-h-[500px] flex flex-col justify-between">
          {/* Footer Content with Wide Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            {/* AI Empower Branding */}
            <motion.div
              className="flex flex-col items-start text-left gap-2"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              <motion.h3
                className="font-bold mb-4 cursor-pointer"
                style={{
                  fontSize: brandingFontSize,
                  background: 'linear-gradient(90deg, #3b82f6, #a855f7, #ec4899)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  color: 'transparent',
                  textShadow: glowIntensity,
                }}
                variants={brandingVariants}
                initial="hidden"
                animate="visible"
                onClick={handleLogoClick}
              >
                {brandingText.split('').map((char, index) => (
                  <motion.span key={index} variants={letterVariants}>
                    {char}
                  </motion.span>
                ))}
              </motion.h3>
              <p className="text-gray-300 text-sm max-w-xs leading-relaxed">
                Providing simplified, affordable, scalable, and customized AI solutions that increase productivity, customer engagement, and growth for businesses globally.
              </p>
            </motion.div>

            {/* Mission Statement */}
            <motion.div
              className="flex flex-col items-center text-center"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              style={{ opacity: useTransform(scrollYProgress, [0, 1], [0.5, 1]), y: useTransform(scrollYProgress, [0, 1], [20, 0]) }}
            >
              <h4 className="text-lg font-semibold text-white mb-4">Our Vision</h4>
              <p className="text-gray-300 text-sm max-w-sm leading-relaxed">
                We envision a future where every business—big or small—can easily tap into the power of AI to work smarter, connect better, and grow stronger. Our goal is to make AI simple, affordable, and within everyone's reach.
              </p>
            </motion.div>

            {/* Enhanced Contact Info */}
            <motion.div
              className="flex flex-col items-end text-right"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              <motion.h4
                className="text-lg font-semibold text-white mb-4 relative"
                style={{ textShadow: contactGlow }}
                animate={{
                  textShadow: [
                    '0 0 5px rgba(168, 85, 247, 0.5)',
                    '0 0 10px rgba(59, 130, 246, 0.7)',
                    '0 0 5px rgba(168, 85, 247, 0.5)',
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity, repeatType: 'loop' }}
              >
                Contact Us
                <motion.div
                  className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-blue-500 to-purple-500"
                  animate={{ backgroundPosition: ['0% 50%', '100% 50%'] }}
                  style={{ backgroundSize: '200% 200%' }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                />
              </motion.h4>
              <motion.div
                className="relative bg-gray-800/50 border border-gray-700/50 rounded-lg p-3 mb-2"
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.9, duration: 0.6 }}
                whileHover={{ scale: 1.1, boxShadow: '0 0 15px rgba(168, 85, 247, 0.5)' }}
                onHoverStart={() => setEmailHover(true)}
                onHoverEnd={() => setEmailHover(false)}
                onClick={() => setEmailClick(!emailClick)}
                style={{
                  rotateY: useTransform(scrollYProgress, [0, 1], [0, 10]),
                }}
              >
                <ContactSparkles trigger={emailHover} />
                <motion.p
                  className="text-gray-200 text-base relative"
                  whileHover={{ color: '#a855f7' }}
                  transition={{ duration: 0.3 }}
                >
                  Email: info@aiempower.ca
                  <motion.span
                    className="absolute -top-8 right-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs px-2 py-1 rounded-md shadow-lg"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: emailHover ? 1 : 0, y: emailHover ? 0 : 10 }}
                    transition={{ duration: 0.3 }}
                  >
                    Send us an email!
                  </motion.span>
                </motion.p>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-purple-600/50 to-blue-600/50 rounded-lg"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: emailClick ? 1.5 : 0, opacity: emailClick ? 0.5 : 0 }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                />
              </motion.div>
              <motion.div
                className="relative bg-gray-800/50 border border-gray-700/50 rounded-lg p-3"
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 1.1, duration: 0.6 }}
                whileHover={{ scale: 1.1, boxShadow: '0 0 15px rgba(59, 130, 246, 0.5)' }}
                onHoverStart={() => setPhoneHover(true)}
                onHoverEnd={() => setPhoneHover(false)}
                onClick={() => setPhoneClick(!phoneClick)}
                style={{
                  rotateY: useTransform(scrollYProgress, [0, 1], [0, 10]),
                }}
              >
                <ContactSparkles trigger={phoneHover} />
                <motion.p
                  className="text-gray-200 text-base relative"
                  whileHover={{ color: '#3b82f6' }}
                  transition={{ duration: 0.3 }}
                >
                  Phone: +1 (647) 482-3509
                  <motion.span
                    className="absolute -top-8 right-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-xs px-2 py-1 rounded-md shadow-lg"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: phoneHover ? 1 : 0, y: phoneHover ? 0 : 10 }}
                    transition={{ duration: 0.3 }}
                  >
                    Give us a call!
                  </motion.span>
                </motion.p>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-purple-600/50 to-blue-600/50 rounded-lg"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: phoneClick ? 1.5 : 0, opacity: phoneClick ? 0.5 : 0 }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                />
              </motion.div>
            </motion.div>
          </div>

          {/* Social Icons and Back to Top */}
          <motion.div
            className="flex flex-col items-center mb-10"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.9 }}
          >
            <motion.div
              className="flex justify-center mb-6"
              style={{ scale: useTransform(scrollYProgress, [0, 1], [1, 1.1]) }}
            >
              <SocialIcon3D
                href="https://www.facebook.com/AIEmpowerConsultants/"
                imageSrc="https://cdn.brandfetch.io/idpKX136kp/theme/dark/idu8FJi_SM.svg?c=1dxbfHSJFAPEGdCLU4o5B"
                alt="Facebook"
                scrollYProgress={scrollYProgress}
              />
              <SocialIcon3D
                href="https://www.instagram.com/_aiempower/"
                imageSrc="https://cdn.brandfetch.io/ido5G85nya/theme/dark/symbol.svg?c=1dxbfHSJFAPEGdCLU4o5B"
                alt="Instagram"
                scrollYProgress={scrollYProgress}
              />
              <SocialIcon3D
                href="https://chat.whatsapp.com/G8kROPWo5q2B9ZQmiWuGdd"
                imageSrc="https://cdn.brandfetch.io/id6Zq084G_/theme/dark/idULKo6n9o.svg?c=1dxbfHSJFAPEGdCLU4o5B"
                alt="WhatsApp"
                scrollYProgress={scrollYProgress}
              />
              <SocialIcon3D
                href="https://www.linkedin.com/company/aiempower-consultants?originalSubdomain=ca"
                imageSrc="https://cdn.brandfetch.io/idJFz6sAsl/theme/dark/idtEseDv1X.svg?c=1dxbfHSJFAPEGdCLU4o5B"
                alt="LinkedIn"
                scrollYProgress={scrollYProgress}
              />
            </motion.div>
            <motion.button
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm font-semibold"
              whileHover={{ scale: 1.15, boxShadow: '0 0 20px rgba(147, 51, 234, 0.6)' }}
              whileTap={{ scale: 0.95 }}
              onClick={handleBackToTop}
              animate={{
                scale: [1, 1.15, 1],
                y: useTransform(scrollYProgress, [0, 1], [0, -5]),
                boxShadow: [
                  '0 0 10px rgba(147, 51, 234, 0.4)',
                  '0 0 15px rgba(147, 51, 234, 0.6)',
                  '0 0 10px rgba(147, 51, 234, 0.4)',
                ],
              }}
              transition={{ duration: 1.5, repeat: Infinity, repeatType: 'loop' }}
            >
              <ArrowUp className="h-4 w-4" />
              Back to Top
            </motion.button>
          </motion.div>

          {/* Copyright Text with Interactive Emoji and Scroll Glow */}
          <motion.div
            className="text-gray-300 text-xs text-center relative"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
            style={{ textShadow: copyrightGlow }}
          >
            <motion.p
              initial={{ color: '#d1d5db' }}
              whileHover={{ color: '#a855f7' }}
              transition={{ duration: 0.3 }}
            >
              © {new Date().getFullYear()} AI Empower Inc. All rights reserved.
            </motion.p>
            <motion.p
              className="mt-1"
              initial={{ opacity: 0 }}
              whileHover={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <span className="text-purple-400">
                Crafted with{' '}
                <motion.span
                  animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.3, 1] }}
                  transition={{ duration: 0.6, repeat: Infinity, repeatType: 'loop' }}
                >
                  ❤️
                </motion.span>{' '}
                and Cutting-Edge AI
              </span>
            </motion.p>
          </motion.div>
        </div>

        {/* Subtle Background Glow */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          animate={{
            boxShadow: 'inset 0 0 40px rgba(147, 51, 234, 0.15), inset 0 0 40px rgba(59, 130, 246, 0.15)',
          }}
          transition={{ duration: 2.5, repeat: Infinity, repeatType: 'reverse' }}
        />
      </motion.footer>
    </div>
  );
}